/**
 * The shell every editor in this dashboard renders into.
 *
 * All of them used to be modals. A modal has no address, so nothing could be
 * linked to, reopened in a second tab, or bookmarked; a stray click on the
 * overlay threw the work away without asking; and the add-on options screen had
 * already reached the state a modal system always reaches, with an option
 * editor stacked on top of a list that was itself on top of a page. As routes
 * they each have an address, a Back that means what it says, and the whole
 * window to lay a bilingual form out in.
 *
 * Two behaviours belong here rather than in each form, because they are the two
 * that get forgotten: a record has to be *fetched* when someone arrives by URL
 * rather than by clicking a row, and unsaved work has to survive a stray Back.
 */

import type { QueryKey } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { apiGet } from '@/lib/api';
import { ApiError } from '@/lib/errors';
import { PageHeader } from './app-shell';
import { ConfirmDialog } from './records';
import { Alert, Button, Card, SkeletonRows } from './ui';

// ------------------------------------------------------------------ record

/**
 * One record, by id.
 *
 * A modal was always handed the row the list had already loaded. A page cannot
 * be — someone arriving at `/menu/items/<uuid>` from a bookmark, a second tab
 * or a reload has no list behind them — so every editor fetches its own record
 * and the list row becomes a convenience, not a dependency.
 */
export function useRecord<T>(key: QueryKey, url: string, id: string | undefined) {
  const query = useQuery({
    queryKey: key,
    queryFn: () => apiGet<T>(`${url}/${id}`),
    enabled: Boolean(id),
    // The record is about to be edited, so it must be the current one: a row
    // cached from the list thirty seconds ago carries a version that may have
    // moved, and saving against it is a 409 with no explanation.
    staleTime: 0,
  });

  return {
    row: query.data?.data,
    isLoading: query.isLoading,
    /** A 404 — or a 400 from the API's UUID pipe on an id that is not one. */
    notFound:
      query.error instanceof ApiError && (query.error.status === 404 || query.error.status === 400),
    error: query.error,
  };
}

/**
 * Wait for the record, then hand it to the form.
 *
 * The form itself is only mounted once the row is in hand, which is what lets
 * every field keep initialising from `useState(row.something)` exactly as it did
 * inside the modal — and what makes the dirty check below compare against the
 * real starting values rather than against a screen full of empty strings.
 */
export function RecordPage<T>({
  title,
  backTo,
  record,
  children,
}: {
  title: ReactNode;
  backTo: string;
  record: ReturnType<typeof useRecord<T>>;
  children: (row: T) => ReactNode;
}) {
  const { t } = useTranslation();

  if (record.isLoading) return <FormLoading title={title} backTo={backTo} />;

  if (!record.row) {
    return (
      <>
        <PageHeader width="narrow" title={title} actions={<BackButton to={backTo} />} />
        <Alert tone="danger">
          {record.notFound ? t('common.noSuchRecord') : t('common.somethingBroke')}
        </Alert>
      </>
    );
  }

  return <>{children(record.row)}</>;
}

// ------------------------------------------------------------------- dirty

/**
 * The form's own loading state.
 *
 * A form is mounted only once everything its initial values come from is in
 * hand — the record, and any list a field defaults out of. A default that
 * arrives a render later would read to `useDirty` as an edit, and an untouched
 * form would start asking whether you really meant to leave it.
 */
export function FormLoading({ title, backTo }: { title: ReactNode; backTo: string }) {
  return (
    <>
      <PageHeader width="narrow" title={title} actions={<BackButton to={backTo} />} />
      <SkeletonRows rows={6} />
    </>
  );
}

/**
 * Has anything changed since this form opened?
 *
 * Compared as JSON against the first snapshot rather than tracked per field. A
 * branch form has eleven inputs; eleven `onChange` flags is eleven chances to
 * forget one, and the one forgotten is the field whose edit gets discarded
 * without a word. The values here are strings, booleans and small plain objects,
 * so serialising them is both cheap and exact.
 */
export function useDirty(values: unknown): boolean {
  const serialized = JSON.stringify(values);
  const initial = useRef(serialized);
  return serialized !== initial.current;
}

// -------------------------------------------------------------- form shell

export function BackButton({ to, onClick }: { to: string; onClick?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Button variant="ghost" size="sm" onClick={onClick ?? (() => navigate(to))}>
      {t('common.back')}
    </Button>
  );
}

export function FormPage({
  title,
  description,
  backTo,
  dirty = false,
  saving = false,
  canSave = true,
  onSave,
  saveLabel,
  footerStart,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Where Back and Cancel go, and where a successful save returns to. */
  backTo: string;
  dirty?: boolean;
  saving?: boolean;
  canSave?: boolean;
  /** Absent for a reader without edit rights — the form renders, Save does not. */
  onSave?: () => void;
  saveLabel?: ReactNode;
  /** Anything that belongs at the start of the footer, e.g. media's Archive. */
  footerStart?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  /*
   * The browser's own guard, for the exits this app does not own: a reload, a
   * closed tab, a typed address. It covers less than a router-level block would
   * — a click on the sidebar still leaves silently, because `useBlocker` needs
   * a data router and this app is a `BrowserRouter` — but it covers the two
   * exits that lose the most work, and it is strictly more than the modal this
   * replaces did, which discarded everything to a stray click on its overlay.
   */
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Every current browser ignores the string and shows its own wording; the
      // assignment is still what arms the prompt in some of them.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const leave = () => {
    if (dirty) setConfirmingLeave(true);
    else navigate(backTo);
  };

  return (
    <>
      <PageHeader
        // Forms cap at a readable measure. A bilingual editor puts two language
        // columns side by side, and past this width they stop being comparable.
        width="narrow"
        title={title}
        description={description}
        actions={<BackButton to={backTo} onClick={leave} />}
      />

      <Card
        footer={
          <>
            {footerStart}
            <Button variant="ghost" onClick={leave}>
              {t('common.cancel')}
            </Button>
            {onSave && (
              <Button variant="primary" loading={saving} disabled={!canSave} onClick={onSave}>
                {saveLabel ?? t('common.save')}
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-4">{children}</div>
      </Card>

      <ConfirmDialog
        open={confirmingLeave}
        title={t('common.unsavedChanges')}
        body={t('common.discardBody')}
        confirmLabel={t('common.discard')}
        onCancel={() => setConfirmingLeave(false)}
        onConfirm={() => {
          setConfirmingLeave(false);
          navigate(backTo);
        }}
      />
    </>
  );
}
